import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import FigoLottie from '../../../common/FigoLottie';
import { ReimbursementDetailsFrame } from '../reimbursementDetails';
import { EmptyScreen } from '../../../common/petCloud/EmptyScreen';
import { ReimbursementMultibrandCard } from './ReimbursementMultibrandCard';

import { getThemeLogoById } from '../../../../theme/theme.utils';
import { MARKETING_CHANNEL_ID } from '../../../../theme/theme.constants';

import { MY_ACCOUNT } from '../../../../constants';
import { useMixpanel, useUserBrands } from '../../../../hooks';
import { formatShortDateByLanguage } from '../../../../services/language.utils';
import { useEditReimbursementEvent } from '../../../../services/mixpanel';

import '../css/ReimbursementMultibrandList.css';

const ReimbursementMultibrandList = ({
  allBrandsData = [],
  breadCrumbs,
  onClickCard = () => { },
}) => {
  const { t } = useTranslation('myAccount');
  const [selectedPolicy, setSelectedPolicy] = useState({});
  const [petId, setPetId] = useState('');
  const [openReimbursementFrame, setOpenReimbursementFrame] = useState(false);
  const { hideReimbursementScreen } = useUserBrands();
  const { sendEvent } = useMixpanel();
  const { EV58, SOURCES, loadingClaims } = useEditReimbursementEvent();

  const policiesToShow = useMemo(() => allBrandsData
    .filter((policy) => policy.MarketingChannelId !== 0
      && policy.MarketingChannelId
      !== MARKETING_CHANNEL_ID.ppiG), [allBrandsData]);

  const renderList = () => {
    if (!allBrandsData.length || !policiesToShow.length) {
      if (hideReimbursementScreen) {
        breadCrumbs.onAddBreadcrumbItem({
          id: MY_ACCOUNT.ppuReimbursementDetails,
        }, true);

        return null;
      }

      return (
        <EmptyScreen
          description={t('reimbursementSection.emptyScreen.description')}
          title={t('reimbursementSection.emptyScreen.title')}
        />
      );
    }

    return (
      <div className="Reimbursement-multibrand-cards-container">
        <p>{t('reimbursementSection.selectPolicy')}</p>

        {!policiesToShow.length && <FigoLottie />}

        {policiesToShow.map((policy) => {
          const startDate = formatShortDateByLanguage(policy.PolicyStartDate);
          const endDate = formatShortDateByLanguage(policy.PolicyEndDate);

          const handleOnClickCard = () => {
            const source = SOURCES.myAccount;

            sendEvent(EV58({ source }));

            if (policy.MarketingChannelId !== MARKETING_CHANNEL_ID.figo) {
              setPetId(policy.Pets[0].PetId);
              setSelectedPolicy({
                coveragePeriodId: policy.CoveragePeriodId,
                marketingChannelId: policy.MarketingChannelId,
                policyId: policy.PolicyId,
                policyNumber: policy.PolicyNumber,
              });
              setOpenReimbursementFrame(true);
              return;
            }

            onClickCard({
              policyInfo: {
                PolicyTerm: `${startDate} - ${endDate}`,
                ...policy,
              },
              policyNumber: policy.PolicyNumber,
            });
          };

          return (
            <ReimbursementMultibrandCard
              key={String(policy.PolicyId)}
              endDate={endDate}
              handleOnClickCard={handleOnClickCard}
              loadingClaims={loadingClaims}
              logo={getThemeLogoById(policy.MarketingChannelId).dark}
              marketChannelId={policy.MarketingChannelId}
              petsInPolicy={policy.Pets}
              policyId={policy.PolicyId}
              policyNumber={policy.PolicyNumber}
              startDate={startDate}
            />
          );
        })}
      </div>
    );
  };

  if (openReimbursementFrame) {
    return (
      <ReimbursementDetailsFrame
        petId={petId}
        policy={selectedPolicy}
      />
    );
  }

  return (
    <div className="Reimbursement-multibrand-screen-container">
      <h1>{t('reimbursementCard.title')}</h1>

      {renderList()}
    </div>
  );
};

export { ReimbursementMultibrandList };
